import React, { useState } from 'react';

interface ValidationInfo {
  isValid: boolean;
  confidence: number;
  issues: string[];
  suggestedValue?: number;
  suggestedUnit?: string;
  explanation: string;
}

interface DataValidationWarningProps {
  testName: string;
  currentValue: number;
  currentUnit: string;
  validation: ValidationInfo;
  onAcceptSuggestion: (value: number, unit: string) => void;
  onDismiss: () => void;
}

export const DataValidationWarning: React.FC<DataValidationWarningProps> = ({
  testName,
  currentValue,
  currentUnit,
  validation,
  onAcceptSuggestion,
  onDismiss
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (validation.isValid) {
    return null;
  }

  const confidencePercent = Math.round(validation.confidence * 100);
  const confidenceColor = validation.confidence > 0.8 ? 'text-green-600' : 
                          validation.confidence > 0.5 ? 'text-yellow-600' : 'text-red-600';

  return (
    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
         onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start gap-2">
        <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-yellow-800">
                Potential Data Issue Detected
              </h4>
              
              <div className="mt-1 text-xs text-yellow-700">
                <p className="mb-1">
                  Current: <span className="font-mono font-medium">{currentValue} {currentUnit}</span>
                </p>
                {validation.suggestedValue !== undefined && (
                  <p className="mb-1">
                    Suggested: <span className="font-mono font-medium text-green-700">
                      {validation.suggestedValue} {validation.suggestedUnit || currentUnit}
                    </span>
                  </p>
                )}
                <p className={`${confidenceColor}`}>
                  Confidence: {confidencePercent}%
                </p>
              </div>

              {/* Issues list */}
              <div className="mt-2">
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-xs text-yellow-600 hover:text-yellow-700 flex items-center gap-1"
                >
                  <span>{isExpanded ? 'Hide' : 'Show'} details</span>
                  <svg className={`w-3 h-3 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {isExpanded && (
                  <div className="mt-2 space-y-1">
                    <div className="text-xs text-yellow-700">
                      <p className="font-medium mb-1">Issues found:</p>
                      <ul className="list-disc list-inside space-y-0.5">
                        {validation.issues.map((issue, idx) => (
                          <li key={idx}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                    {validation.explanation && (
                      <div className="text-xs text-gray-600 mt-2 p-2 bg-white rounded border border-yellow-100">
                        <p className="font-medium mb-0.5">Explanation:</p>
                        <p>{validation.explanation}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Action buttons */}
              {validation.suggestedValue !== undefined && (
                <div className="mt-3 flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent event bubbling
                      // Accept the suggestion and dismiss
                      onAcceptSuggestion(
                        validation.suggestedValue!,
                        validation.suggestedUnit || currentUnit
                      );
                      onDismiss(); // Also dismiss the warning
                    }}
                    className="text-xs bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 
                             flex items-center gap-1 transition-all"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Accept Suggestion
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDismiss(); // Just dismiss without making changes
                    }}
                    className="text-xs bg-gray-200 text-gray-700 px-3 py-1.5 rounded hover:bg-gray-300 
                             flex items-center gap-1 transition-all"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Keep Original
                  </button>
                </div>
              )}
            </div>
            
            <button
              onClick={onDismiss}
              className="ml-2 text-yellow-600 hover:text-yellow-800"
              title="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};